import { useState, useEffect, useCallback } from "react";
import { Bot, Camera, MessageCircle, Brain, Plus, Trash2, ChevronDown, ChevronUp, Users, TrendingUp, BookOpen, Clock, Send, Loader2, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function AIDashboardPage() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [activeTab, setActiveTab] = useState("stats");
  const [expandedSession, setExpandedSession] = useState(null);
  const [newQ, setNewQ] = useState("");
  const [newA, setNewA] = useState("");
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, sessRes, anaRes, knRes] = await Promise.all([
        fetch(`${API_BASE}/api/ai/stats`, { headers }),
        fetch(`${API_BASE}/api/chat-sessions`, { headers }),
        fetch(`${API_BASE}/api/hair-analyses`, { headers }),
        fetch(`${API_BASE}/api/ai/knowledge`, { headers })
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (sessRes.ok) setSessions(await sessRes.json());
      if (anaRes.ok) setAnalyses(await anaRes.json());
      if (knRes.ok) setKnowledge(await knRes.json());
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addKnowledge = async () => {
    if (!newQ.trim() || !newA.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API_BASE}/api/ai/knowledge`, {
        method: "POST", headers,
        body: JSON.stringify({ question: newQ, answer: newA })
      });
      if (res.ok) { setNewQ(""); setNewA(""); fetchAll(); }
    } catch (e) { console.error(e); }
    finally { setAdding(false); }
  };

  const deleteKnowledge = async (id) => {
    try {
      await fetch(`${API_BASE}/api/ai/knowledge/${id}`, { method: "DELETE", headers });
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const formatDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return d; }
  };

  const tabs = [
    { id: "stats", label: "الإحصائيات", icon: TrendingUp },
    { id: "chats", label: "المحادثات", icon: MessageCircle },
    { id: "analyses", label: "تحليلات الشعر", icon: Camera },
    { id: "knowledge", label: "تدريب البوت", icon: Brain }
  ];

  const filteredSessions = sessions.filter(s =>
    !searchTerm || s.messages?.some(m => m.text?.includes(searchTerm))
  );

  return (
    <div className="space-y-6" data-testid="ai-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">لوحة الذكاء الاصطناعي</h1>
          <p className="text-gray-500 text-sm mt-1">إدارة الشات بوت وتحليلات الشعر</p>
        </div>
        <Bot className="w-10 h-10 text-amber-500" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="ai-tabs">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.id ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid={`tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* Stats Tab */}
      {activeTab === "stats" && stats && (
        <div className="space-y-6" data-testid="stats-panel">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "محادثات اليوم", value: stats.chats.today, icon: MessageCircle, color: "bg-blue-50 text-blue-600", iconBg: "bg-blue-100" },
              { label: "محادثات الأسبوع", value: stats.chats.week, icon: TrendingUp, color: "bg-amber-50 text-amber-600", iconBg: "bg-amber-100" },
              { label: "إجمالي المحادثات", value: stats.chats.total, icon: Users, color: "bg-green-50 text-green-600", iconBg: "bg-green-100" },
              { label: "تحليلات الشعر", value: stats.analyses.total, icon: Camera, color: "bg-purple-50 text-purple-600", iconBg: "bg-purple-100" }
            ].map((s, i) => (
              <div key={i} className={`p-5 rounded-2xl ${s.color}`} data-testid={`stat-card-${i}`}>
                <div className={`w-10 h-10 ${s.iconBg} rounded-xl flex items-center justify-center mb-3`}><s.icon className="w-5 h-5" /></div>
                <p className="text-3xl font-bold">{s.value}</p>
                <p className="text-sm mt-1 opacity-80">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border p-5">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-blue-500" />ملخص المحادثات</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm"><span className="text-gray-500">اليوم</span><span className="font-bold">{stats.chats.today}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">هذا الأسبوع</span><span className="font-bold">{stats.chats.week}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">هذا الشهر</span><span className="font-bold">{stats.chats.month}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">الإجمالي</span><span className="font-bold">{stats.chats.total}</span></div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border p-5">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2"><Brain className="w-5 h-5 text-amber-500" />قاعدة المعرفة</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm"><span className="text-gray-500">عدد الإجابات المخصصة</span><span className="font-bold">{stats.knowledge_count}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">تحليلات اليوم</span><span className="font-bold">{stats.analyses.today}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">تحليلات الأسبوع</span><span className="font-bold">{stats.analyses.week}</span></div>
              </div>
              <button onClick={() => setActiveTab("knowledge")} className="mt-4 w-full py-2 bg-amber-50 text-amber-600 rounded-xl text-sm font-medium hover:bg-amber-100 transition-colors">
                إضافة إجابات جديدة
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chats Tab */}
      {activeTab === "chats" && (
        <div className="space-y-4" data-testid="chats-panel">
          <div className="relative">
            <Search className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="بحث في المحادثات..." className="w-full pr-10 pl-4 py-3 border rounded-xl text-sm focus:border-amber-400 focus:outline-none" data-testid="chat-search" />
          </div>
          {filteredSessions.length === 0 ? (
            <div className="text-center py-12 text-gray-400"><MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>لا توجد محادثات بعد</p></div>
          ) : (
            filteredSessions.map((session, idx) => (
              <div key={session.session_id || idx} className="bg-white rounded-2xl border overflow-hidden" data-testid={`session-${idx}`}>
                <button onClick={() => setExpandedSession(expandedSession === idx ? null : idx)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3 text-right">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"><MessageCircle className="w-5 h-5 text-blue-500" /></div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">{session.messages?.[0]?.text?.slice(0, 50) || "محادثة"}...</p>
                      <p className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(session.created_at)} - {session.messages?.length || 0} رسائل</p>
                    </div>
                  </div>
                  {expandedSession === idx ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>
                {expandedSession === idx && (
                  <div className="border-t p-4 bg-gray-50 space-y-3 max-h-96 overflow-y-auto">
                    {session.messages?.map((msg, mIdx) => (
                      <div key={mIdx} className={`flex ${msg.role === "user" ? "justify-start" : "justify-end"}`}>
                        <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${msg.role === "user" ? "bg-amber-100 text-amber-900" : "bg-white border text-gray-700"}`}>
                          <p className="text-[10px] font-bold mb-1 opacity-60">{msg.role === "user" ? "الزائر" : "البوت"}</p>
                          {msg.text}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Analyses Tab */}
      {activeTab === "analyses" && (
        <div className="space-y-4" data-testid="analyses-panel">
          {analyses.length === 0 ? (
            <div className="text-center py-12 text-gray-400"><Camera className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>لا توجد تحليلات بعد</p></div>
          ) : (
            analyses.map((a, idx) => (
              <div key={a.id || idx} className="bg-white rounded-2xl border p-5" data-testid={`analysis-${idx}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Camera className="w-5 h-5 text-purple-500" />
                  <span className="text-sm font-bold text-gray-800">تحليل #{idx + 1}</span>
                  <span className="text-xs text-gray-400 mr-auto">{formatDate(a.created_at)}</span>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{a.analysis}</div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Knowledge Tab */}
      {activeTab === "knowledge" && (
        <div className="space-y-6" data-testid="knowledge-panel">
          {/* Add New */}
          <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5">
            <h3 className="font-bold text-amber-800 mb-1 flex items-center gap-2"><Brain className="w-5 h-5" />تدريب الشات بوت</h3>
            <p className="text-amber-600 text-xs mb-4">أضف أسئلة وأجوبة مخصصة. البوت سيتعلم منها ويستخدمها في الردود على الزوار</p>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-amber-800 block mb-1">السؤال المتوقع</label>
                <input value={newQ} onChange={e => setNewQ(e.target.value)} placeholder="مثال: كم مدة الضمان على العملية؟" className="w-full px-4 py-3 rounded-xl border border-amber-200 text-sm focus:border-amber-400 focus:outline-none bg-white" data-testid="knowledge-question" />
              </div>
              <div>
                <label className="text-sm font-medium text-amber-800 block mb-1">الإجابة الصحيحة</label>
                <textarea value={newA} onChange={e => setNewA(e.target.value)} placeholder="مثال: نقدم ضمان مدى الحياة على الشعر المزروع. الشعر المزروع لا يتساقط أبداً..." rows={3} className="w-full px-4 py-3 rounded-xl border border-amber-200 text-sm focus:border-amber-400 focus:outline-none bg-white resize-none" data-testid="knowledge-answer" />
              </div>
              <button onClick={addKnowledge} disabled={adding || !newQ.trim() || !newA.trim()} className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-400 disabled:opacity-40 transition-colors" data-testid="knowledge-add-btn">
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                إضافة للبوت
              </button>
            </div>
          </div>

          {/* Existing Knowledge */}
          <div>
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5 text-gray-500" />قاعدة المعرفة ({knowledge.length})</h3>
            {knowledge.length === 0 ? (
              <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-2xl"><Brain className="w-10 h-10 mx-auto mb-2 opacity-50" /><p className="text-sm">لا توجد إجابات مخصصة. أضف أول إجابة!</p></div>
            ) : (
              <div className="space-y-3">
                {knowledge.map((entry) => (
                  <div key={entry.id} className="bg-white rounded-2xl border p-4" data-testid={`knowledge-${entry.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-bold text-gray-800 mb-1">س: {entry.question}</p>
                        <p className="text-sm text-gray-600">ج: {entry.answer}</p>
                        <p className="text-xs text-gray-400 mt-2">{formatDate(entry.created_at)} - {entry.added_by}</p>
                      </div>
                      <button onClick={() => deleteKnowledge(entry.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-knowledge-${entry.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
